import React, { ReactElement, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { State } from '../../dux/@types';
import {
  getSelectedTeamIds,
  setSelectedTeamIds,
  clearSelectedTeamIds,
  getTeamMode,
  getOrgIdForTeamMode,
} from '../../dux';
import { TeamAPI } from '../../api';
import filteredTeamMembershipsForSelector from '../../helpers/TeamHelper';

const SHARE_WITH = 'Share with ';
const DELETE_FROM = 'Delete from ';

type TeamSelectorProps = {
  initialSelection?: number[];
  singleSelect: boolean;
  mention?: boolean;
  disableCurrentTeam?: boolean;
  excludePersonalWorkspace?: boolean;
  shouldDelete?: boolean;
  onSelectionChange?: (teamIds: number[]) => void;
  statusMessage?: string | null;
  /** When provided, restricts available teams to only those in this list (used for replies) */
  allowedTeamIds?: number[];
};

const TeamSelector = (props: TeamSelectorProps): ReactElement => {
  const {
    initialSelection,
    singleSelect,
    mention,
    onSelectionChange,
    statusMessage,
    disableCurrentTeam = false,
    excludePersonalWorkspace = false,
    shouldDelete = false,
    allowedTeamIds,
  } = props;
  const { data: teamMemberships } =
    TeamAPI.endpoints.getUsersTeamMemberships.useQuery(undefined);
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const teamModeId = useSelector(getTeamMode);
  const teamModeOrgId = useSelector(getOrgIdForTeamMode);
  const selectedTeamIds = useSelector(getSelectedTeamIds);
  const dispatch = useDispatch();

  const MENTION_TEAMS_TEXT = `The user(s) you've mentioned will only be able to see your note in the workspace(s) of which they are a member.`;
  const labelText = shouldDelete ? DELETE_FROM : SHARE_WITH;

  const teams = useMemo(() => {
    let workspaces = filteredTeamMembershipsForSelector(
      teamMemberships,
      orgUser,
      teamModeOrgId,
    );

    if (excludePersonalWorkspace && orgUser) {
      workspaces = workspaces.filter((team) => team.id !== orgUser.teamId);
    }

    // When allowedTeamIds is provided, restrict to only those teams (used for replies)
    if (allowedTeamIds && allowedTeamIds.length > 0) {
      workspaces = workspaces.filter((team) => allowedTeamIds.includes(team.id));
    }

    return workspaces;
  }, [
    teamMemberships,
    orgUser,
    teamModeOrgId,
    excludePersonalWorkspace,
    allowedTeamIds,
  ]);

  // TODO: fix adding initialSelection to dependencies
  useEffect(() => {
    if (initialSelection) {
      dispatch(setSelectedTeamIds(initialSelection));
    } else {
      dispatch(setSelectedTeamIds([teamModeId]));
    }
    return () => {
      dispatch(clearSelectedTeamIds());
    };
  }, [dispatch, teamModeId]);

  const handleSelectAll = (): void => {
    const allTeamIds = teams.map((team) => team.id);
    dispatch(setSelectedTeamIds(allTeamIds));
    if (onSelectionChange) {
      onSelectionChange(allTeamIds);
    }
  };

  const handleDeselectAll = (): void => {
    dispatch(setSelectedTeamIds([]));
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { checked, id } = event.target;
    const teamId = parseInt(id.slice(5), 10);

    const nextSelectedTeamIds = (() => {
      if (checked) {
        return singleSelect ? [teamId] : [...selectedTeamIds, teamId];
      }
      if (!singleSelect) {
        return selectedTeamIds.filter((t) => t !== teamId);
      }
      return selectedTeamIds;
    })();

    dispatch(setSelectedTeamIds(nextSelectedTeamIds));

    if (onSelectionChange) {
      onSelectionChange(nextSelectedTeamIds);
    }
  };

  return (
    <>
      {statusMessage && (
        <div className="alert alert-warning team-selector-alert" role="alert">
          {statusMessage}
        </div>
      )}
      <div className="team-selector">
        {teams.map((team) => {
          const shareWith = `${orgUser && team.id === orgUser.teamId ? '' : labelText}${
            team.name
          }`;
          const teamInputId = `team_${team.id}`;
          const type = singleSelect ? 'radio' : 'checkbox';
          return (
            <div className="anno-visibility mt-2 d-flex" key={team.id}>
              <input
                checked={selectedTeamIds.includes(team.id)}
                id={teamInputId}
                name={singleSelect ? 'singleSelect' : team.name}
                onChange={handleChange}
                type={type}
                value={team.id}
                // Disable the checkbox for the current team id if mentions are in the comment or disableCurrentTeam is explicitly set to true
                disabled={
                  (mention || disableCurrentTeam) && team.id === teamModeId
                    ? true
                    : false
                }
              />
              <label htmlFor={teamInputId}>{shareWith}</label>
            </div>
          );
        })}
      </div>
      {!singleSelect && (
        <div className="d-flex justify-content-between mt-2">
          <button className="btn btn-link p-0" onClick={handleSelectAll} type="button">
            Select All
          </button>
          <button
            className="btn btn-link p-0 text-muted"
            onClick={handleDeselectAll}
            type="button"
          >
            Deselect All
          </button>
        </div>
      )}
      {mention &&
        selectedTeamIds.includes(teamModeId) &&
        selectedTeamIds.length > 1 && (
          <div className="alert alert-warning warning m-1 mt-2">
            {MENTION_TEAMS_TEXT}
          </div>
        )}
    </>
  );
};

TeamSelector.defaultProps = {
  singleSelect: false,
};

export default TeamSelector;
